// Error-report fallback record (Story #4613, Stream 8b). When a screen crashes,
// the SupportErrorBoundary writes a SANITISED record to sessionStorage; the
// New Request form reads it (null-guarded) to pre-fill a bug report. Only the
// error message, a truncated component stack, the URL, and a timestamp are kept
// — never arbitrary app state or PII.

import { CRASH_REPORT_KEY } from '../../constants/support'

export interface CrashReport {
  message: string
  componentStack: string
  url: string
  at: string
}

export function writeCrashReport(error: Error, componentStack: string): void {
  try {
    const record: CrashReport = {
      message: String(error?.message || 'Unexpected error').slice(0, 500),
      componentStack: String(componentStack || '').slice(0, 1500),
      url: typeof window !== 'undefined' ? window.location.pathname + window.location.search : '',
      at: new Date().toISOString(),
    }
    sessionStorage.setItem(CRASH_REPORT_KEY, JSON.stringify(record))
  } catch {
    /* sessionStorage unavailable — the boundary still renders its safe fallback */
  }
}

/**
 * Read the crash record, tolerating a missing OR unparseable value: either
 * yields `null` so callers render the degraded-but-safe variant rather than
 * throwing a second time inside the terminal boundary.
 */
export function readCrashReport(): CrashReport | null {
  try {
    const raw = sessionStorage.getItem(CRASH_REPORT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    return {
      message: typeof parsed.message === 'string' ? parsed.message : '',
      componentStack: typeof parsed.componentStack === 'string' ? parsed.componentStack : '',
      url: typeof parsed.url === 'string' ? parsed.url : '',
      at: typeof parsed.at === 'string' ? parsed.at : '',
    }
  } catch {
    return null
  }
}

export function clearCrashReport(): void {
  try {
    sessionStorage.removeItem(CRASH_REPORT_KEY)
  } catch {
    /* non-fatal */
  }
}

/** Build a bug-report prefill from the crash record, degrading safely when absent. */
export function crashReportPrefill(): { requestType: 'bug'; subject: string; description: string } {
  const rec = readCrashReport()
  if (!rec || !rec.message) {
    return {
      requestType: 'bug',
      subject: 'Something went wrong in the app',
      description:
        'The app hit an unexpected error. (No diagnostic details were captured — please describe what you were doing when it happened.)',
    }
  }
  return {
    requestType: 'bug',
    subject: `App error: ${rec.message}`.slice(0, 120),
    description:
      `An unexpected error occurred${rec.url ? ` on ${rec.url}` : ''}${rec.at ? ` at ${rec.at}` : ''}.\n\n` +
      `Error: ${rec.message}\n\nWhat I was doing when it happened:\n`,
  }
}
